<template>
  <SrpModal :isVisible="isVisible" :isClosable="false" :isCloseButtonVisible="false" className="new-social-account-added-modal" :width="540" size="small">
    <template #header>
      <h2 class="global-h2" style="margin: 0">{{ title }}</h2>
    </template>
    <template #content>
      <div v-if="accountsToShow.length" class="new-social-account-added-modal__accounts">
        <div v-for="account in accountsToShow" :key="account.channelType" class="new-social-account-added-modal__account">
          <IconEmbedded :name="channelToIcon(account.channelType)" :size="22" />
          <span class="new-social-account-added-modal__account-label">{{ getSocialChannelLabel(account.channelType) }}</span>
          <span v-if="account.displayName" class="new-social-account-added-modal__account-name">· {{ account.displayName }}</span>
        </div>
      </div>
      <p class="global-p" style="margin-top: 16px; margin-bottom: 8px">We'll update unapproved posts to:</p>
      <ul class="global-p new-social-account-added-modal__updates">
        <li>Add {{ pendingList.length === 1 ? "this account" : "these accounts" }}</li>
        <li v-if="showTaggingNote">Tag locations for {{ pendingList.length === 1 ? "this account" : "these accounts" }}</li>
      </ul>
      <div v-if="showApprovedToggle" class="new-social-account-added-modal__toggle-row">
        <SrpSwitch v-model:isChecked="addToApprovedPosts" :isDisabled="isSaving || isLoadingStatus" />
        <span class="new-social-account-added-modal__toggle-label">Also update posts I've approved</span>
        <InfoIconWithTooltip :width="280">Toggle this off if you don't want changes to posts that have been approved for publishing.</InfoIconWithTooltip>
      </div>
      <p v-if="errorMessage" class="global-error-string" style="margin-top: 12px; margin-bottom: 0">{{ errorMessage }}</p>
    </template>
    <template #footer>
      <SrpButton :isDisabled="isSaving || isLoadingStatus" @click="submit">
        <template #icon>
          <LoadingIcon v-if="isSaving || isLoadingStatus" :size="18" />
          <IconEmbedded v-else name="check_3" :size="18" />
        </template>
        Continue
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpSwitch from "@components/ui/SrpSwitch.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import SocialMediaSettingsRepo from "@repos/SocialMediaSettingsRepo";
import { MetricSender } from "@helpers/MetricSender";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { getSocialChannelLabel, type SocialChannels } from "@contracts/socialNetworks";
import type { ApplyPendingChannelsResponse, PendingChannelsPromptStatus, SocialChannelData } from "@contracts/socialMediaSettings";
import type { IconName } from "@/contracts/IconName";

const props = withDefaults(
  defineProps<{
    customerId: string;
    pendingChannels?: SocialChannels[] | null;
    socialChannels?: SocialChannelData[];
  }>(),
  {
    pendingChannels: () => [],
    socialChannels: () => [],
  }
);

const emit = defineEmits<{
  (e: "applied", response: ApplyPendingChannelsResponse): void;
}>();

const addToApprovedPosts = ref(true);
const isSaving = ref(false);
const isLoadingStatus = ref(false);
const errorMessage = ref("");
const promptStatus = ref<PendingChannelsPromptStatus | null>(null);
let statusLoadGeneration = 0;

const pendingList = computed(() => (props.pendingChannels ?? []).filter(Boolean));
const isVisible = computed(() => pendingList.value.length > 0);
const showApprovedToggle = computed(() => !isLoadingStatus.value && (promptStatus.value?.eligibleApprovedPostCount ?? 0) > 0);
const showTaggingNote = computed(() => !isLoadingStatus.value && promptStatus.value?.disableAutoTagging === false);

const title = computed(() => (pendingList.value.length === 1 ? "Account Successfully Added!" : "Accounts Successfully Added!"));

const accountsToShow = computed(() => {
  return pendingList.value.map(channelType => {
    const match = (props.socialChannels ?? []).find(channel => channel.channelType === channelType);
    return {
      channelType,
      displayName: match?.displayName || match?.username || "",
    };
  });
});

watch(
  isVisible,
  async visible => {
    if (!visible) {
      isLoadingStatus.value = false;
      promptStatus.value = null;
      return;
    }
    const generation = ++statusLoadGeneration;
    isLoadingStatus.value = true;
    addToApprovedPosts.value = true;
    errorMessage.value = "";
    promptStatus.value = null;
    try {
      const status = await SocialMediaSettingsRepo.getPendingChannelsStatus(props.customerId);
      if (generation !== statusLoadGeneration) return;
      promptStatus.value = status;
      addToApprovedPosts.value = status.eligibleApprovedPostCount > 0;
    } catch (err) {
      if (generation !== statusLoadGeneration) return;
      promptStatus.value = { eligibleApprovedPostCount: 1, disableAutoTagging: false };
      addToApprovedPosts.value = true;
      getGlobalRemoteLogger()?.error(`Failed to load pending social channel status for ${props.customerId}: ${err}`);
    } finally {
      if (generation === statusLoadGeneration) isLoadingStatus.value = false;
    }
  },
  { immediate: true }
);

function channelToIcon(channelType: string): IconName {
  switch (channelType) {
    case "facebook":
      return "facebook";
    case "instagram":
      return "instagram";
    case "twitter":
      return "xwitter";
    case "linkedin":
      return "linkedin";
    case "tiktok":
      return "tiktok";
    case "youtube":
      return "youtube";
    default:
      return "share-up_2";
  }
}

async function submit() {
  if (isSaving.value || isLoadingStatus.value) return;
  isSaving.value = true;
  errorMessage.value = "";
  try {
    const addApproved = showApprovedToggle.value && addToApprovedPosts.value;
    const response = await SocialMediaSettingsRepo.applyPendingChannels(props.customerId, addApproved);
    MetricSender.sendMetric("Cust-Calendar-PendingChannels-Apply", {
      customerId: props.customerId,
      addToApprovedPosts: addApproved,
      pendingChannels: pendingList.value,
      updatedPostCount: response.updatedPostCount,
    });
    emit("applied", response);
  } catch (err) {
    errorMessage.value = "Couldn't update posts. Please try again.";
    getGlobalRemoteLogger()?.error(`Failed to apply pending social channels for ${props.customerId}: ${err}`);
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped lang="scss">
.new-social-account-added-modal {
  &__accounts {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__account {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__account-label {
    font-weight: 600;
  }

  &__account-name {
    color: rgba(0, 0, 0, 0.6);
  }

  &__updates {
    margin: 0;
    padding-left: 22px;
  }

  &__toggle-row {
    margin-top: 18px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__toggle-label {
    font-weight: 600;
  }
}
</style>
